<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  title: { type: String, default: '' },
  prefixIcon: { type: String, default: '' },
  prefixIconClass: { type: String, default: 'text-lais-primary' },
  // 'tight' = padding ajustado pra cards com lista (perfil do cliente)
  // 'cozy'  = padding padrão pra cards com texto
  density: {
    type: String,
    default: 'cozy',
    validator: v => ['cozy', 'tight', 'flush'].includes(v),
  },
  href: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  active: { type: Boolean, default: false },
  interactive: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  ariaLabel: { type: String, default: '' },
  expandable: { type: Boolean, default: false },
  startExpanded: { type: Boolean, default: true },
  expandLabel: { type: String, default: 'Expandir conteúdo' },
  collapseLabel: { type: String, default: 'Recolher conteúdo' },
});

const emit = defineEmits(['click', 'toggle']);
const isExpanded = ref(props.startExpanded);

const isActionable = computed(
  () => props.href || props.interactive || props.selectable
);
const rootTag = computed(() => {
  if (props.href && !props.disabled) return 'a';
  if (isActionable.value) return 'button';

  return 'article';
});

const rootClass = computed(() => [
  'flex flex-col overflow-hidden rounded-lais border bg-lais-bg-card shadow-lais-card transition',
  {
    'border-lais-border-soft': !props.selected && !props.active,
    'border-lais-primary ring-2 ring-lais-primary-50':
      props.selected || props.active,
    'cursor-pointer text-left hover:border-lais-primary/50 hover:shadow-lais-pop focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50':
      isActionable.value && !props.disabled,
    'cursor-not-allowed opacity-60': props.disabled,
  },
]);

const handleClick = event => {
  if (props.disabled) {
    event.preventDefault();
    return;
  }

  emit('click', event);
};

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
  emit('toggle', isExpanded.value);
};
</script>

<template>
  <component
    :is="rootTag"
    :href="rootTag === 'a' ? href : undefined"
    :type="rootTag === 'button' ? 'button' : undefined"
    :disabled="rootTag === 'button' ? disabled : undefined"
    :aria-label="ariaLabel || undefined"
    :aria-pressed="selectable ? selected : undefined"
    :aria-current="active ? 'true' : undefined"
    :aria-disabled="disabled && rootTag === 'a' ? 'true' : undefined"
    :tabindex="disabled && rootTag === 'a' ? -1 : undefined"
    :class="rootClass"
    @click="handleClick"
  >
    <header
      v-if="
        $slots.header ||
        title ||
        prefixIcon ||
        expandable ||
        $slots.titleAction ||
        $slots.suffix
      "
      class="flex items-center justify-between gap-3 px-5 pt-4 pb-3 border-b border-lais-border-soft"
    >
      <slot name="header">
        <div class="flex min-w-0 items-center gap-2">
          <span
            v-if="prefixIcon"
            class="size-4 shrink-0"
            :class="[prefixIcon, prefixIconClass]"
            aria-hidden="true"
          />
          <h2
            v-if="title"
            class="truncate text-lais-heading-sm font-semibold leading-tight text-lais-text font-redHatDisplay"
          >
            {{ title }}
          </h2>
        </div>
      </slot>
      <div
        v-if="$slots.titleAction || $slots.suffix || expandable"
        class="flex items-center gap-2"
      >
        <slot name="titleAction">
          <slot name="suffix" />
        </slot>
        <button
          v-if="expandable"
          type="button"
          class="grid size-8 shrink-0 place-items-center rounded-full text-lais-text-muted transition hover:bg-lais-primary-50 hover:text-lais-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50"
          :aria-label="isExpanded ? collapseLabel : expandLabel"
          :aria-expanded="isExpanded"
          @click.stop="toggleExpanded"
        >
          <span
            class="i-lucide-chevron-down size-4 transition-transform"
            :class="isExpanded ? 'rotate-180' : ''"
            aria-hidden="true"
          />
        </button>
      </div>
    </header>
    <div
      v-show="!expandable || isExpanded"
      :class="{
        'p-5': density === 'cozy',
        'px-5 py-4': density === 'tight',
        'p-0': density === 'flush',
      }"
    >
      <slot />
    </div>
    <footer
      v-if="$slots.footer"
      class="px-5 py-4 border-t border-lais-border-soft bg-lais-bg-card"
    >
      <slot name="footer" />
    </footer>
  </component>
</template>
